import {
  Diseases,
  type DiseaseType,
  Findings,
  type FindingType,
} from "../mappers";

export interface DiseasePredictionModel {
  predict: (findings: FindingType[]) => Record<DiseaseType, number>;
}

const QUALITATIVE_VALUE_TO_PROBABILITIES_MAP = {
  likely: 5.0,
  indeterminate: 1.2,
  unlikely: 0.2,
} as const; // numerical weights were assigned based on their impact on model performance, more info here: https://elephant.slab.com/posts/fever-guide-test-recommendations-doodling-e5zvxn56#hmtah-final-strategy-naive-bayesian-prediction-using-qualitative-symptom-likelihoods-and-a-disease-probability-threshold

export type QualitativeProbability =
  keyof typeof QUALITATIVE_VALUE_TO_PROBABILITIES_MAP;

export type DiseaseFindingParameters = Record<
  DiseaseType,
  Partial<Record<FindingType, QualitativeProbability>>
>;

export class NaiveBayesDiseaseModel implements DiseasePredictionModel {
  constructor(
    private diseaseFindingParameters: DiseaseFindingParameters = DISEASE_FINDING_PARAMETERS,
    private qualitativeValueToProbabilitesMap: Record<
      QualitativeProbability,
      number
    > = QUALITATIVE_VALUE_TO_PROBABILITIES_MAP,
  ) {}

  predict(findings: FindingType[]): Record<DiseaseType, number> {
    const diseaseScores: Record<DiseaseType, number> = Object.keys(
      this.diseaseFindingParameters,
    ).reduce(
      (acc, disease) => {
        acc[disease as DiseaseType] = 1.0; // initialize disease scores with a neutral likelihood of 1.0
        return acc;
      },
      {} as Record<DiseaseType, number>,
    );

    // calculate disease likelihoods
    for (const finding of findings) {
      for (const disease in this.diseaseFindingParameters) {
        if (this.diseaseFindingParameters[disease]?.[finding]) {
          const qualitativeProbability =
            this.diseaseFindingParameters[disease][finding];
          diseaseScores[disease as DiseaseType] *=
            this.qualitativeValueToProbabilitesMap[qualitativeProbability];
        }
      }
    }

    // normalise into probabilities
    const totalScore = Object.values(diseaseScores).reduce(
      (sum, score) => sum + score,
      0,
    );
    if (totalScore > 0) {
      for (const disease in diseaseScores) {
        diseaseScores[disease as DiseaseType] /= totalScore;
      }
    }

    return diseaseScores;
  }
}

const DISEASE_FINDING_PARAMETERS: DiseaseFindingParameters = {
  [Diseases.malaria]: {
    [Findings.cough]: "likely",
    [Findings.headache]: "likely",
    [Findings.breathless]: "likely",
    [Findings.vomiting]: "likely",
    [Findings.diarrhoea]: "indeterminate",
    [Findings.chest_pain]: "indeterminate",
    [Findings.stomach_pain]: "likely",
    [Findings.rash]: "unlikely",
    [Findings.muscle_ache]: "likely",
    [Findings.pain_on_passing_urine]: "unlikely",
    [Findings.blood_in_urine]: "unlikely",
  },
  [Diseases.typhoid]: {
    [Findings.cough]: "likely",
    [Findings.headache]: "likely",
    [Findings.vomiting]: "unlikely",
    [Findings.diarrhoea]: "indeterminate",
    [Findings.stomach_pain]: "likely",
    [Findings.rash]: "likely",
    [Findings.muscle_ache]: "likely",
    [Findings.pain_on_passing_urine]: "unlikely",
    [Findings.blood_in_urine]: "unlikely",
  },
  [Diseases.dengue]: {
    [Findings.cough]: "unlikely",
    [Findings.headache]: "likely",
    [Findings.vomiting]: "likely",
    [Findings.diarrhoea]: "unlikely",
    [Findings.chest_pain]: "unlikely",
    [Findings.stomach_pain]: "indeterminate",
    [Findings.rash]: "likely",
    [Findings.muscle_ache]: "likely",
    [Findings.pain_on_passing_urine]: "unlikely",
    [Findings.blood_in_urine]: "indeterminate",
  },
  [Diseases.uti]: {
    [Findings.cough]: "unlikely",
    [Findings.headache]: "unlikely",
    [Findings.vomiting]: "indeterminate",
    [Findings.diarrhoea]: "unlikely",
    [Findings.stomach_pain]: "indeterminate",
    [Findings.rash]: "unlikely",
    [Findings.muscle_ache]: "unlikely",
    [Findings.pain_on_passing_urine]: "likely",
    [Findings.blood_in_urine]: "likely",
  },
};
